declare global {
	const jQl: any;
}

import { DOCUMENT, isPlatformBrowser, isPlatformServer, NgIf } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	Inject,
	Input,
	OnDestroy,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { SanitizeHtmlPipe } from '@woolworthsnz/styleguide';

@Component({
	selector: 'dc-html',
	template: `
		<div
			*ngIf="content"
			[innerHTML]="content | sanitizeHtml"
			class="dynamic-content2-item dynamic-content2-item-html"
			#html
		></div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, SanitizeHtmlPipe],
})
export class HtmlComponent implements AfterViewInit, OnDestroy {
	@HostBinding('style.display') display: 'block' | 'none' = 'none';
	@ViewChild('html') html: ElementRef;

	private scripts = new Set<DocumentFragment>();
	private _content: string;

	constructor(
		private cdr: ChangeDetectorRef,
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) private platformId: Object
	) {}

	get content(): string {
		return this._content;
	}

	@Input() set content(c: string) {
		// when set on the browser via innerHtml any script tags won't get evaluated - so we need to pull them out and insert via appendChild which will execute them!

		const scriptTags = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/g;

		const loadingScript = `<script data-insert-id="jql-loader">
				if (!window.dc_jq_loaded) {
					jQl.loadjQ('/assets/jquery-3.7.1.min.js');
					jQl.loadjQdep('/assets/jquery-ui.min.js');
					window.dc_jq_loaded = true;
				}
			</script>`;

		// if the script tag contains jQuery use the jq loader to load it
		const scriptContainsJQuery = /.*(\$|jQuery).*/g;

		let resultArray: RegExpExecArray | null;
		let containsjQuery = false;
		let bodyScript = '';

		while ((resultArray = scriptTags.exec(c)) !== null) {
			bodyScript = resultArray[0].replace('<script ', "<script data-insert-id='dc-inserted-html-script' ");
			bodyScript = bodyScript.replace('<script>', "<script data-insert-id='dc-inserted-html-script'>");
			if (resultArray[0].match(scriptContainsJQuery)) {
				containsjQuery = true;
			}

			if (isPlatformBrowser(this.platformId)) {
				const fragment = this.document.createRange().createContextualFragment(bodyScript);
				this.scripts.add(fragment);
			}
		}

		if (isPlatformServer(this.platformId)) {
			if (containsjQuery && !this.document.querySelector('[data-insert-id=jql-loader]')) {
				// when on the server we can just add this block to the set content and pass back to the browser// there is no need to handle scripts differently on SSR since the scripts will be executed on the client as they get sent back as part of a legitimate static response
				this._content = `${c}${loadingScript}`;
			} else {
				this._content = c;
			}

			return;
		}

		// the rest is only run when on the browser...

		if (containsjQuery && !this.document.querySelector('[data-insert-id=jql-loader]')) {
			this.document.body.appendChild(this.document.createRange().createContextualFragment(loadingScript));
		}

		// now add the scripts to the body on next tick
		setTimeout(() => {
			this.scripts.forEach((f) => (f = this.document.body.appendChild(f)));
		}, 25);

		// no point keeping the script string in the content that will be set
		c = c.replace(scriptTags, '');

		this._content = c;
	}

	ngAfterViewInit(): void {
		// Avoid an expressionChangeAfterItHasBeenChecked error
		setTimeout(() => (this.display = this.toggleShowHide()), 1);
	}

	ngOnDestroy(): void {
		this.document
			.querySelectorAll("[data-insert-id='dc-inserted-html-script']")
			.forEach((el) => el.parentNode?.removeChild(el));
	}

	toggleShowHide = (): 'block' | 'none' => {
		if (!this.html) {
			return 'none';
		}

		let hasElementsToBeShown = false;

		(Array.from(this.html.nativeElement.children) || []).forEach((c: any) => {
			if (c.tagName !== 'SCRIPT' && c.tagName !== 'STYLE') {
				hasElementsToBeShown = true;
			}
		});

		this.cdr.markForCheck();

		return hasElementsToBeShown ? 'block' : 'none';
	};
}
